import { VarietyResponse } from '../../variety/models';
import { BatchDto } from '../../batch/models';
import { ProductionActivityResponse } from '../../production-activity/models';

// Aliases for consistency with existing models
type VarietyDto = VarietyResponse;
type ProductionActivityDto = ProductionActivityResponse;

/**
 * Location type enum
 * FARM is the highest level, followed by PARCEL, BLOCK, and ROW
 */
export enum LocationType {
  FARM = 'FARM',
  PARCEL = 'PARCEL',
  BLOCK = 'BLOCK',
  ROW = 'ROW'
}

/**
 * Location reference in harvest record
 */
export interface LocationReference {
  locationType: LocationType;
  locationId: number;
  locationName?: string; // Populated by backend
}

/**
 * Harvest record assignee DTO
 */
export interface HarvestRecordAssigneeDto {
  id: number;
  assigneeName: string;
  assigneeType: 'USER' | 'WORKER';
  originalUserId?: number;
  originalWorkerId?: number;
}

/**
 * Harvest Record DTO
 */
export interface HarvestRecordDto {
  id: number;
  recordDate?: string; // ISO date string (YYYY-MM-DD)
  recordTime?: string; // ISO time string (HH:mm:ss)
  quantity?: number;       // net weight (computed on backend)
  brutoQuantity?: number;  // gross weight (as entered)
  boxes?: number;
  comment?: string;
  assignees: HarvestRecordAssigneeDto[];
  variety?: VarietyDto;
  batch?: BatchDto;
  linkedProductionActivity?: ProductionActivityDto;
  locations: LocationReference[];
  plantingYear?: number; // Extracted from ROW location

  // Audit fields
  createdDate: string; // ISO instant (UTC)
  createdBy: string;
  modifiedDate?: string; // ISO instant (UTC)
  modifiedBy?: string;

  // Localized timestamps
  createdAtLocal: string; // ISO offset datetime
  modifiedAtLocal?: string; // ISO offset datetime
}

/**
 * Create harvest record request
 */
export interface CreateHarvestRecordRequest {
  recordDate?: string; // ISO date string (YYYY-MM-DD)
  recordTime?: string; // ISO time string (HH:mm:ss)
  brutoQuantity?: number; // gross weight; backend computes net
  quantity?: number;
  boxes?: number;
  comment?: string;
  userIds?: number[];
  workerIds?: number[];
  teamIds?: number[];
  varietyId?: number;
  linkedProductionActivityId?: number;
  locations: LocationReference[];
  autoPopulateFromContext?: boolean; // Auto-populate user, variety, production activity from context
}

/**
 * Update harvest record request
 */
export interface UpdateHarvestRecordRequest {
  id: number;
  recordDate?: string; // ISO date string (YYYY-MM-DD)
  recordTime?: string; // ISO time string (HH:mm:ss)
  brutoQuantity?: number; // gross weight; backend computes net
  quantity?: number;
  boxes?: number;
  comment?: string;
  userIds?: number[];
  workerIds?: number[];
  teamIds?: number[];
  varietyId?: number;
  linkedProductionActivityId?: number;
  locations: LocationReference[];
}

/**
 * Harvest record filters for list queries
 */
export interface HarvestRecordFilters {
  batchId?: number;
  recordDate?: string; // ISO date string
  startDate?: string; // ISO date string
  endDate?: string; // ISO date string
  userId?: number;
  workerId?: number;
  varietyId?: number;
  productionActivityId?: number;
}

/**
 * Daily harvest summary for analytics
 */
export interface DailyHarvestSummary {
  date: string;
  quantity: number;
  recordCount: number;
}

/**
 * Harvest record analytics DTO
 */
export interface HarvestRecordAnalyticsDto {
  totalQuantity: number;
  recordCount: number;
  averagePerDay: number;
  dailyBreakdown: DailyHarvestSummary[];
}
